import styled from 'styled-components';
import {YouTubeEmbed} from 'components/YouTubeEmbed';
import {CodeInPlaceLink} from '../CodeInPlaceLink';
import {PrereqCheck} from './PrereqCheck';
import {SplashFaq} from './SplashFaq';
import {
  FaAward,
  FaBrain,
  FaClock,
  FaDumbbell,
  FaMap,
  FaUserFriends,
} from 'react-icons/fa';

/** DOM id the splash scrolls to when its hero CTA is clicked. */
export const COURSE_INFO_ID = 'pai-course-info';

/** The course-intro YouTube video: https://youtu.be/gJaE29DR0gs */
const INTRO_VIDEO_ID = 'gJaE29DR0gs';

/**
 * Responsive 16:9 video frame, matching the same wrapper idiom used around
 * {@link YouTubeEmbed} elsewhere (e.g. `DemoVideoModal`,
 * `BuddyIntroSequence`): the box sizes itself from `aspect-ratio` alone, and
 * the CSS override below stretches the embed's fixed pixel width/height
 * attrs to fill it.
 */
const VideoFrame = styled.div`
  width: 100%;
  max-width: 720px;
  aspect-ratio: 16 / 9;
  margin: 1.5rem 0;
  border-radius: 8px;
  overflow: hidden;

  iframe {
    width: 100%;
    height: 100%;
  }
`;

/**
 * The PAI course-information section, laid out exactly like the Code in Place
 * homepage body (webapp `landing/home/LandingContent.tsx`): the same
 * `d-flex flex-column align-items-center` outer wrapper and
 * `landing-container` / Bootstrap `row`/`col` inner structure, with
 * `aboutIcon` fact icons and `blue` stat spans — all styled by the webapp's
 * global `style.scss` + Bootstrap, which the PAI app shell already loads.
 *
 * IMPORTANT: must be rendered OUTSIDE `HeroSplash`'s styled `Splash` wrapper.
 * That wrapper applies a blanket `* { margin: 0; padding: 0 }` reset to its
 * descendants, which collapses the Bootstrap-grid spacing this section relies
 * on — the same reason the shared `SiteFooter` is kept a sibling of it.
 *
 * @return The course-information section.
 * @throws Never.
 */
export function CourseInfo() {
  return (
    <section
      id={COURSE_INFO_ID}
      aria-label="About the course"
      className="d-flex flex-column align-items-center"
    >
      <div className="landing-container">
        <div className="row">
          <div className="col">
            <h2 style={{paddingTop: '50px'}}>The PAI Course - Oct 2026</h2>
            <p>
              Learn the probability behind modern AI, online for free, with the
              support of thousands of teachers — the same human-centered model
              that has powered <CodeInPlaceLink /> since 2020.
            </p>
            <ul className="list-unstyled">
              <li className="mb-2">
                <FaUserFriends className="aboutIcon" aria-hidden="true" />{' '}
                <strong>Who?</strong> Anyone curious about how AI really works.
                Comfort with algebra is all you need.
              </li>
              <li className="mb-2">
                <FaMap className="aboutIcon" aria-hidden="true" />{' '}
                <strong>Where?</strong> Anywhere with internet.
              </li>
              <li className="mb-2">
                <FaBrain className="aboutIcon" aria-hidden="true" />{' '}
                <strong>What?</strong> The core probability that powers modern
                machine learning and AI.
              </li>
              <li className="mb-2">
                <FaClock className="aboutIcon" aria-hidden="true" />{' '}
                <strong>When?</strong> Applications due the last week of
                September. Class starts October 9th.
              </li>
              <li className="mb-2">
                <FaAward className="aboutIcon" aria-hidden="true" />{' '}
                <strong>Certificate?</strong> A public portfolio of your work
                hosted by Stanford.
              </li>
              <li className="mb-2">
                <FaDumbbell className="aboutIcon" aria-hidden="true" />{' '}
                <strong>How much work?</strong> A few focused hours each week
                for 6 weeks. Set your own schedule.
              </li>
            </ul>
            <p>
              Probability is the language of modern AI. From the uncertainty in
              a medical diagnosis to the randomness inside a large language
              model, the ideas in this course are the foundation today's AI is
              built on. PAI teaches them the way Stanford teaches them — with
              intuition, real applications, and programming — and it is for
              everyone from humanists and social scientists to hardcore
              engineers.
            </p>
            <p>
              What makes the <CodeInPlaceLink /> way special? We recruit and
              train one volunteer teacher for every ten students, creating a
              vibrant community of teaching and learning. We believe the number
              of people who want to teach is large — perhaps roughly
              proportional to the number who want to learn. Teaching is joyful,
              and it is the best way to learn, both the content and how to lead
              a team.
            </p>
            <p>
              Students and section leaders come from all over the world and from
              every kind of background. “Everyone is welcome” is one of our
              mottos.
            </p>
            <VideoFrame>
              <YouTubeEmbed video={INTRO_VIDEO_ID} width={720} height={405} />
            </VideoFrame>
            <h2 style={{paddingTop: '20px'}}>Course Outline</h2>
            <p>
              Six weeks, each pairing the core probability you learn with a
              hands-on project you build.
            </p>
            <table className="table-hover table">
              <thead>
                <tr>
                  <th scope="col">
                    <h5>
                      <span className="badge text-bg-primary">Week #</span>
                    </h5>
                  </th>
                  <th scope="col">
                    <h5>
                      <span className="badge text-bg-primary">You Learn</span>
                    </h5>
                  </th>
                  <th scope="col">
                    <h5>
                      <span className="badge text-bg-primary">You Build</span>
                    </h5>
                  </th>
                </tr>
              </thead>
              <tbody className="table-group-divider">
                <tr>
                  <th scope="row">Week 1</th>
                  <td>Core Probability</td>
                  <td>
                    An inference-time resource decision maker for large AI
                    models
                  </td>
                </tr>
                <tr>
                  <th scope="row">Week 2</th>
                  <td>Random variables</td>
                  <td>Probabilistic artwork</td>
                </tr>
                <tr>
                  <th scope="row">Week 3</th>
                  <td>Maximum likelihood estimation</td>
                  <td>
                    Find hidden chambers in the pyramids, a cell-phone tracker,
                    and more!
                  </td>
                </tr>
                <tr>
                  <th scope="row">Week 4</th>
                  <td>Logistic regression</td>
                  <td>A test suite to see where it works and fails</td>
                </tr>
                <tr>
                  <th scope="row">Week 5</th>
                  <td>Neural networks</td>
                  <td>Visualize how large models work under the hood</td>
                </tr>
                <tr>
                  <th scope="row">Week 6</th>
                  <td>Probabilistic evaluation</td>
                  <td>
                    Advanced critiques of how AI models are performing, such as
                    fairness and calibration
                  </td>
                </tr>
              </tbody>
            </table>
            <p>
              The course culminates in a final project of your own design.
              You'll bring together the probability and AI ideas from all six
              weeks to build something you care about — and it becomes the
              centerpiece of the public portfolio you take with you.
            </p>
            <h2 style={{paddingTop: '20px'}}>Frequently asked questions</h2>
            <p>…with honest answers.</p>
            <SplashFaq />
            <h2 style={{paddingTop: '20px'}}>Are you ready?</h2>
            <p>Answer a few questions to see where you fit.</p>
            <PrereqCheck />
          </div>
        </div>
      </div>
    </section>
  );
}
